/**
 * 결함 목록 필터 · 검색 · 통계 — 순수 함수
 */
import { resolveDefectTypeDisplayName } from '@/utils/defect/defectTypeDisplay';

/**
 * parentDefectTypes → code/seq → name 빠른 조회 맵
 * @param {object[]} parentDefectTypes
 * @returns {Record<string,string>}
 */
export function buildDefectTypeLabelMap(parentDefectTypes) {
  const map = {};
  (parentDefectTypes || []).forEach(t => {
    const code = t.code ?? t.seq ?? t.id;
    if (code != null && t.name) {
      map[String(code).toLowerCase()] = t.name;
    }
  });
  return map;
}

/**
 * 지도 툴바에서 선택한 결함 유형만 모듈 리스트에 표시.
 * 필터가 비어 있으면 전체 유형(변경 없음).
 *
 * @param {object[]} defects
 * @param {object[]} toolbarDefectFilters — DotSolarMapBox defectFilters 와 동일
 * @param {{ resolveDefectTypeRowId: (row: object) => number|null }} helpers
 */
export function filterDefectsByToolbarTypes(defects, toolbarDefectFilters, helpers = {}) {
  const filters = Array.isArray(toolbarDefectFilters) ? toolbarDefectFilters : [];
  if (!filters.length) {
    return defects || [];
  }
  const { resolveDefectTypeRowId } = helpers;
  if (typeof resolveDefectTypeRowId !== 'function') {
    return defects || [];
  }

  const allowedNumericIds = new Set();
  const allowedCodes = new Set();
  filters.forEach((dt) => {
    const id = resolveDefectTypeRowId(dt);
    if (id != null) {
      allowedNumericIds.add(String(id));
    }
    if (dt?.code) {
      allowedCodes.add(String(dt.code).toLowerCase());
    }
  });

  return (defects || []).filter((d) => {
    if (d?.defectCode != null && allowedNumericIds.has(String(d.defectCode))) {
      return true;
    }
    const typeStr = String(d?.defectType ?? '').toLowerCase();
    if (!typeStr) {
      return false;
    }
    if (allowedCodes.has(typeStr)) {
      return true;
    }
    return allowedNumericIds.has(typeStr);
  });
}

/** wfsDefectRows 출처 — module_defects(확정) vs ai_detect_defects(레이어 전용) */
export const WFS_DEFECT_SOURCE = {
  CONFIRMED: 'confirmed',
  AI_ONLY: 'ai-only'
};

/**
 * AI 탐지 결함 행 여부 (WFS detectWay: 'A' | 'AI')
 * @param {object} defect
 * @returns {boolean}
 */
export function isAiDetectDefectRow(defect) {
  const way = String(defect?.detectWay ?? '').toUpperCase();
  return way === 'A' || way === 'AI';
}

/**
 * AI 레이어 전용 행 — 모듈 리스트에서 AI OFF 시 숨김 대상
 * 확정 module_defects 행(detectWay A 포함)은 제외
 */
export function isAiOnlyWfsDefectRow(defect) {
  if (defect?.wfsSource === WFS_DEFECT_SOURCE.CONFIRMED) {
    return false;
  }
  if (defect?.wfsSource === WFS_DEFECT_SOURCE.AI_ONLY) {
    return true;
  }
  // 레거시(태그 없음): detectWay 기준
  return isAiDetectDefectRow(defect);
}

/**
 * AI 표시 토글에 맞춰 결함 목록 필터
 * - ON: 전체
 * - OFF: ai-only 레이어 행만 제외 (확정 결함은 detectWay A 여도 유지)
 */
export function filterDefectsByAiVisibility(defects, showAiDefectFlag) {
  if (showAiDefectFlag) {
    return defects || [];
  }
  return (defects || []).filter(d => !isAiOnlyWfsDefectRow(d));
}

/**
 * 분류 필터 (상호 배타적)
 * @param {object[]} defects
 * @param {'all'|'ai'|'manual'|'mapped'} filterType
 * @returns {object[]}
 */
export function filterDefectsByType(defects, filterType) {
  if (filterType === 'all' || !filterType) return defects;
  return (defects || []).filter(d => {
    switch (filterType) {
      case 'ai':
        return isAiDetectDefectRow(d);
      case 'manual':
        return !isAiDetectDefectRow(d);
      case 'mapped':
        return d.solarModuleSeq != null && d.solarModuleSeq !== '';
      default:
        return true;
    }
  });
}

/**
 * 검색어로 결함 필터링
 * @param {object[]} defects
 * @param {string} searchText
 * @param {object} helpers
 * @param {(seq: *) => string} [helpers.thermalImageFileName]
 * @returns {object[]}
 */
export function filterDefectsBySearch(defects, searchText, helpers = {}) {
  if (!searchText) return defects;
  const q = searchText.toLowerCase();
  const { thermalImageFileName } = helpers;
  return (defects || []).filter(d => {
    const imgName = typeof thermalImageFileName === 'function' ? thermalImageFileName(d) : '';
    return (
      String(d.seq || '').includes(q) ||
      String(d.defectType || '').toLowerCase().includes(q) ||
      String(d.solarModuleSeq || '').includes(q) ||
      String(d.moduleId ?? d.module_id ?? '').includes(q) ||
      imgName.toLowerCase().includes(q)
    );
  });
}

/**
 * 결함 객체 → defectType label (O(1) lookup)
 * @param {object} item
 * @param {Record<string,string>} labelMap — buildDefectTypeLabelMap 결과
 * @returns {string}
 */
export function fastDefectLabel(item, labelMap) {
  if (!item) return '';
  const code = item.defectCode ?? item.defectType;
  if (code != null) {
    const cached = labelMap[String(code).toLowerCase()];
    if (cached) return cached;
  }
  if (item.defectDesc) return item.defectDesc;
  if (item.defectType && typeof item.defectType === 'string') return item.defectType;
  return '';
}

/**
 * 결함 유형별 카운트 (mapped / AI / manual / total)
 */
export function countMapped(defects) {
  return (defects || []).filter(d => d.solarModuleSeq != null && d.solarModuleSeq !== '').length;
}

export function countAi(defects) {
  return (defects || []).filter(d => isAiDetectDefectRow(d)).length;
}

export function countManual(defects) {
  return (defects || []).filter(d => !isAiDetectDefectRow(d)).length;
}

export { resolveDefectTypeDisplayName };
